import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { ConfirmDialog } from '../../../../common/views/modals/confirmDialog/ConfirmDialog';
import { SerializedKitAsset } from '../../../../../redux/api/fymAPI.types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { isColoursOnly, staleOldTheme } from './staleAssets';
import { useRemakeStaleAssets } from './useRemakeStaleAssets';
import { themeMessages } from './themeMessages';
import { Panel } from '../../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StaleThemeNoteProps {
  accountId: number;
  catalogItemUuid: string;
  themeId: ReleaseThemeId;
  /** The ready current assets made in another theme (`staleKitAssets`). Not empty. */
  stale: SerializedKitAsset[];
  /** "Show them": the Assets tab, where each such card says "Made in <Theme>". */
  onShow: () => void;
}

/**
 * The note for a song whose assets use an older theme or older colours (design q2b, spec §6.4): "N assets of this
 * song use Clean. New assets use Hype." with "Show them" and "Make them again". A theme change
 * makes no asset again; this button does, on request, after a dry run shows the cost.
 */
const StaleThemeNoteComponent: React.FC<StaleThemeNoteProps> = ({
  accountId,
  catalogItemUuid,
  themeId,
  stale,
  onShow,
}) => {
  const intl = useIntl();
  const assetUuids = useMemo(() => stale.map((asset) => asset.uuid), [stale]);
  const remake = useRemakeStaleAssets({ accountId, catalogItemUuid, assetUuids });
  const themeName = intl.formatMessage(releaseThemeMessages[themeId].name);
  const oldTheme = staleOldTheme(stale);
  const coloursOnly = isColoursOnly(stale, themeId);
  const count = stale.length;
  const credits = remake.preview?.credits;

  return (
    <Panel nested className={styles.staleNote} role="status" data-testid="theme-stale-note">
      <div className={styles.grow}>
        <b className={styles.staleStrong}>
          {coloursOnly && intl.formatMessage(themeMessages.staleColours, { count })}
          {!coloursOnly && oldTheme === null && intl.formatMessage(themeMessages.staleMixed, { count })}
          {!coloursOnly &&
            oldTheme !== null &&
            intl.formatMessage(themeMessages.staleOne, {
              count,
              oldTheme: intl.formatMessage(releaseThemeMessages[oldTheme].name),
            })}
        </b>{' '}
        {coloursOnly
          ? intl.formatMessage(themeMessages.staleNewColours)
          : intl.formatMessage(themeMessages.staleNew, { theme: themeName })}
      </div>
      <div className={styles.staleActions}>
        <SMLButton onClick={onShow}>{intl.formatMessage(themeMessages.showThem)}</SMLButton>
        <SMLButton variant="primary" onClick={remake.open}>
          {intl.formatMessage(themeMessages.makeAgain)}
        </SMLButton>
      </div>
      <ConfirmDialog
        isOpen={remake.isOpen}
        tone="neutral"
        title={intl.formatMessage(themeMessages.remakeTitle, { count, theme: themeName })}
        description={intl.formatMessage(themeMessages.remakeDescription)}
        confirmLabel={intl.formatMessage(themeMessages.remakeConfirm, { credits: credits ?? 0 })}
        isBusy={remake.isRemaking}
        isConfirmDisabled={remake.preview === undefined}
        error={remake.error}
        onConfirm={remake.confirm}
        onCancel={remake.cancel}
        testId="theme-remake-dialog"
      >
        {remake.isChecking && <p className={styles.remakeCost}>{intl.formatMessage(themeMessages.remakeChecking)}</p>}
        {credits !== undefined && (
          <p className={styles.remakeCost} data-testid="theme-remake-cost">
            {intl.formatMessage(themeMessages.remakeCost, { credits })}
          </p>
        )}
      </ConfirmDialog>
    </Panel>
  );
};

export const StaleThemeNote = TypedMemo(StaleThemeNoteComponent);
